import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/shadcn/sidebar';
import { cn } from '@/lib/utils';
import { NavGroup } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

export function NavMain({ groups = [] }: { groups: NavGroup[] }) {
    const page = usePage();
    const abilities = ((page as any)?.props?.auth?.abilities || []) as string[]; // eslint-disable-line @typescript-eslint/no-explicit-any

    const currentPath = page.url.split('?')[0] ?? page.url;
    const currentQueryString = page.url.includes('?') ? (page.url.split('?')[1] ?? '') : '';
    const currentSearchParams = new URLSearchParams(currentQueryString);

    const isActiveItem = (url: string) => {
        const itemPath = url.split('?')[0] ?? url;
        return currentPath === itemPath;
    };

    const isActiveChildItem = (url: string) => {
        const itemPath = url.split('?')[0] ?? url;
        if (currentPath !== itemPath) {
            return false;
        }

        const childQueryString = url.includes('?') ? (url.split('?')[1] ?? '') : '';
        if (!childQueryString) {
            return page.url === url;
        }

        const childSearchParams = new URLSearchParams(childQueryString);
        const childCountryType = childSearchParams.get('country_type');

        if (childCountryType) {
            return currentSearchParams.get('country_type') === childCountryType;
        }

        return page.url === url;
    };

    return (
        <>
            {groups.map((group) => (
                <NavGroupSection
                    key={group.title}
                    group={group}
                    abilities={abilities}
                    isActiveItem={isActiveItem}
                    isActiveChildItem={isActiveChildItem}
                />
            ))}
        </>
    );
}

function NavGroupSection({
    group,
    abilities,
    isActiveItem,
    isActiveChildItem,
}: {
    group: NavGroup;
    abilities: string[];
    isActiveItem: (url: string) => boolean;
    isActiveChildItem: (url: string) => boolean;
}) {
    const [open, setOpen] = useState(group.collapsible ? (group.defaultOpen ?? false) : true);

    const items = group.items.filter((item) => !item.requiredPermission || abilities.includes(item.requiredPermission));
    if (items.length === 0) {
        return null;
    }

    const GroupIcon = group.icon;

    return (
        <SidebarGroup className="px-2 py-0">
            {group.collapsible ? (
                <SidebarGroupLabel asChild>
                    {/* eslint-disable-next-line no-restricted-syntax -- intentional label-styled disclosure toggle, not an action button */}
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        aria-expanded={open}
                        className="hover:text-foreground flex w-full items-center gap-1.5"
                    >
                        {GroupIcon && <GroupIcon className="size-3.5 shrink-0" />}
                        <span>{group.title}</span>
                        <ChevronRight className={cn('ml-auto size-3.5 shrink-0 transition-transform', open && 'rotate-90')} />
                    </button>
                </SidebarGroupLabel>
            ) : (
                <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            )}

            {(!group.collapsible || open) && (
                <SidebarMenu>
                    {items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild isActive={isActiveItem(item.url)}>
                                <Link href={item.url} prefetch>
                                    {item.icon && <item.icon />}
                                    <span>{item.title}</span>
                                </Link>
                            </SidebarMenuButton>

                            {!!item.children?.length && (
                                <SidebarMenuSub>
                                    {item.children
                                        .filter((child) => !child.requiredPermission || abilities.includes(child.requiredPermission))
                                        .map((child) => (
                                            <SidebarMenuSubItem key={`${item.title}-${child.title}`}>
                                                <SidebarMenuSubButton asChild isActive={isActiveChildItem(child.url)}>
                                                    <Link href={child.url} prefetch>
                                                        <span>{child.title}</span>
                                                    </Link>
                                                </SidebarMenuSubButton>
                                            </SidebarMenuSubItem>
                                        ))}
                                </SidebarMenuSub>
                            )}
                        </SidebarMenuItem>
                    ))}
                </SidebarMenu>
            )}
        </SidebarGroup>
    );
}
